import { DebugLog } from "../debug/log";
import type { Critique, Idea, Persona, Settings } from "../data/types";
import { isReadyForCloud } from "../data/types";
import type { ModelInfo } from "../data/modelStore";
import { getActiveEngine } from "./active";
import {
  buildCritiqueSystemPrompt,
  buildCritiqueUserMessage,
  extractFirstJson,
} from "./prompts";

const TAG = "engine.critique";

interface RawCritique {
  problem_fit_score?: number;
  switch_likelihood_score?: number;
  reaction_summary?: string;
  questions_back?: string[];
  dealbreakers?: string[];
  must_haves?: string[];
}

export type CritiqueOutcome =
  | { kind: "success"; critique: Critique }
  | { kind: "error"; personaId: string; message: string };

async function critiqueOne(
  idea: Idea,
  persona: Persona,
  settings: Settings,
  installedModel: ModelInfo | null,
): Promise<CritiqueOutcome> {
  const engine = getActiveEngine(settings, installedModel);
  if (!engine) {
    return { kind: "error", personaId: persona.id, message: "No engine configured." };
  }
  DebugLog.i(TAG, `critique ${persona.name}`);
  const result = await engine.complete({
    messages: [
      { role: "system", content: buildCritiqueSystemPrompt(persona) },
      { role: "user", content: buildCritiqueUserMessage(idea, persona) },
    ],
    maxTokens: 1024,
    temperature: 0.5,
  });
  if (result.kind === "error") {
    return { kind: "error", personaId: persona.id, message: result.message };
  }
  const parsed = extractFirstJson<RawCritique>(result.text);
  if (!parsed || typeof parsed !== "object") {
    return {
      kind: "error",
      personaId: persona.id,
      message: "Engine didn't return a parseable critique JSON object.",
    };
  }
  const critique: Critique = {
    personaId: persona.id,
    problemFitScore: clampScore(parsed.problem_fit_score),
    switchLikelihoodScore: clampScore(parsed.switch_likelihood_score),
    reactionSummary: String(parsed.reaction_summary ?? "").trim(),
    questionsBack: Array.isArray(parsed.questions_back)
      ? parsed.questions_back.map(String)
      : [],
    dealbreakers: Array.isArray(parsed.dealbreakers)
      ? parsed.dealbreakers.map(String)
      : [],
    mustHaves: Array.isArray(parsed.must_haves) ? parsed.must_haves.map(String) : [],
  };
  return { kind: "success", critique };
}

function clampScore(value: unknown): number {
  const n = typeof value === "number" ? Math.round(value) : Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(1, Math.min(5, n));
}

export type ProgressCallback = (done: number, total: number) => void;

/**
 * Runs critiques for all personas. Cloud engines run in parallel since each
 * request is independent and the API is thread-safe. On-device Gemma is
 * GPU-bound and not safe for concurrent calls, so we run those serially.
 */
export async function generateCritiques(
  idea: Idea,
  personas: Persona[],
  settings: Settings,
  installedModel: ModelInfo | null,
  onProgress?: ProgressCallback,
): Promise<{ critiques: Critique[]; errors: { personaId: string; message: string }[] }> {
  const total = personas.length;
  onProgress?.(0, total);
  const collected: Critique[] = [];
  const errors: { personaId: string; message: string }[] = [];

  const isCloud = isReadyForCloud(settings);
  if (isCloud) {
    const results = await Promise.all(
      personas.map((p) => critiqueOne(idea, p, settings, installedModel)),
    );
    for (const r of results) {
      if (r.kind === "success") collected.push(r.critique);
      else errors.push({ personaId: r.personaId, message: r.message });
    }
    onProgress?.(total, total);
  } else {
    for (let i = 0; i < personas.length; i++) {
      const r = await critiqueOne(idea, personas[i], settings, installedModel);
      if (r.kind === "success") collected.push(r.critique);
      else errors.push({ personaId: r.personaId, message: r.message });
      onProgress?.(i + 1, total);
    }
  }
  return { critiques: collected, errors };
}
